import { Select } from '@radix-ui/themes';
import clsx from 'clsx';
import { Speech } from 'lucide-react';
import { type ComponentProps } from 'react';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertPopover from 'components/HubertUI/HubertPopover';
import HubertTooltip from 'components/HubertUI/HubertTooltip';
import MutedText from 'components/common/MutedText';
import { useQuestionEditorContext } from '../context/create-question.context';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionIsReadonly, selectQuestionLanguage } from 'rtk/questionEditorSelector';
import { updateOption } from 'rtk/Question.Editor.Slice';
import type { TOptionRole } from '@hubertai/screening-question-types';

interface Props extends ComponentProps<typeof HubertPopover> {
  role?: TOptionRole | null;
  optionIndex: number;
}

const SelectOptionRolePopover = ({ role, optionIndex, ...props }: Props) => {
  const { questionId } = useQuestionEditorContext();
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const dispatch = useAppDispatch();
  const mainContent = document.getElementById('main-content') as HTMLElement;

  return (
    <HubertPopover {...props}>
      <HubertTooltip>
        <HubertTooltip.Trigger asChild>
          <HubertPopover.Trigger asChild>
            <HubertButton
              variant="secondary"
              className="cursor-pointer"
              icon={<Speech className={role ? 'text-primary-500' : 'text-gray-500'} />}
            ></HubertButton>
          </HubertPopover.Trigger>
        </HubertTooltip.Trigger>
        {/* TODO(i18n): builder-only label, not extracted yet */}
        <HubertTooltip.Content>Yes/No role</HubertTooltip.Content>
      </HubertTooltip>
      <HubertPopover.Content
        className={clsx('flex flex-col gap-3', 'max-w-[420px] min-w-[360px]', 'p-[18px]')}
        portalContent={true}
        portalElement={mainContent}
      >
        {/* TODO(i18n): builder-only copy, not extracted yet */}
        <div className="mr-4">
          <span className="font-medium">Option role</span>
          <MutedText className="pt-1 text-sm font-normal text-gray-500">
            Tag this option as Yes or No. When a single-select's options are exactly a Yes + No pair, the voice
            assistant speaks only the question (it skips reading the options aloud), and spoken answers are matched to
            the right option by meaning rather than by label.
          </MutedText>
        </div>

        <Select.Root
          disabled={isReadOnly}
          value={role ?? 'none'}
          onValueChange={(value) => {
            if (optionIndex < 0) return;
            dispatch(
              updateOption({
                questionId,
                language: questionLanguage,
                index: optionIndex,
                field: 'role',
                value: value === 'none' ? undefined : value,
              }),
            );
          }}
        >
          <Select.Trigger className="w-[160px]" placeholder="No role" />
          <Select.Content>
            <Select.Item value="none">No role</Select.Item>
            <Select.Item value="yes">Yes</Select.Item>
            <Select.Item value="no">No</Select.Item>
          </Select.Content>
        </Select.Root>
      </HubertPopover.Content>
    </HubertPopover>
  );
};

export default SelectOptionRolePopover;
